import { ThunkAction } from 'redux-thunk';
import * as Bixby from './../../core';
import { AppState } from './..';
import {
  PORTFOLIO_LOADING,
  PORTFOLIO_SUCCESS,
  PORTFOLIO_FAILURE,
  COMPANY_LOADING,
  COMPANY_FAILURE,
  COMPANY_SUCCESS,
  COMPANY_FINANCIAL_LOADING,
  COMPANY_FINANCIAL_SUCCESS,
  COMPANY_FINANCIAL_FAILURE,
  SET_ALERTS_ENABLED_FAILURE,
  SET_ALERTS_ENABLED_SUCCESS,
  SAVE_CLIENT_EBITDA_LOADING,
  SAVE_CLIENT_EBITDA_SUCCESS,
  SAVE_CLIENT_EBITDA_FAILURE,
  CREATE_DEAL_NOTE_LOADING,
  CREATE_DEAL_NOTE_SUCCESS,
  CREATE_DEAL_NOTE_FAILURE,
  DEAL_NOTES_LOADING,
  DEAL_NOTES_SUCCESS,
  DEAL_NOTES_FAILURE,
} from './types';
import { AccountService, CompanyService, DraftCompanyService } from '$/services';
import { toastError, toastSuccess } from '$/store/toast/actions';

export const loadPortfolioAction = () => {
  return {
    type: PORTFOLIO_LOADING,
  };
};

export const portfolioSuccessAction = (payload: Bixby.Models.CompanyPortfolio[]) => {
  return {
    type: PORTFOLIO_SUCCESS,
    payload,
  };
};

export const portfolioFailureAction = (error: Bixby.Models.BixbyError) => {
  return {
    type: PORTFOLIO_FAILURE,
    payload: error,
    error: true,
  };
};

export const loadCompanyAction = () => {
  return {
    type: COMPANY_LOADING,
  };
};

export const companySuccessAction = (payload?: Bixby.Models.Company) => {
  return {
    type: COMPANY_SUCCESS,
    payload,
  };
};

export const companyFailureAction = (payload: Bixby.Models.BixbyError) => {
  return {
    type: COMPANY_FAILURE,
    payload,
  };
};

export const loadCompanyFinancialAction = (payload: Bixby.Models.FinancialStatementLoading) => {
  return {
    type: COMPANY_FINANCIAL_LOADING,
    payload,
  };
};

export const companyFinancialSuccessAction = (payload: Bixby.Models.FinancialStatement) => {
  return {
    type: COMPANY_FINANCIAL_SUCCESS,
    payload,
  };
};

export const companyFinancialFailureAction = (payload: Bixby.Models.FinancialStatementError) => {
  return {
    type: COMPANY_FINANCIAL_FAILURE,
    payload,
  };
};

export const setAlertsEnabledSuccessAction = (companyId: string) => {
  return {
    type: SET_ALERTS_ENABLED_SUCCESS,
    payload: companyId,
  };
};

export const setAlertsEnabledFailureAction = (payload: Bixby.Models.BixbyError) => {
  return {
    type: SET_ALERTS_ENABLED_FAILURE,
    payload,
  };
};

export const loadSaveClientEbitdaAction = () => {
  return {
    type: SAVE_CLIENT_EBITDA_LOADING,
  };
};

export const setSaveClientEbitdaSuccessAction = (payload?: Bixby.Models.Company) => {
  return {
    type: SAVE_CLIENT_EBITDA_SUCCESS,
    payload,
  };
};

export const setSaveClientEbitdaFailureAction = (payload: Bixby.Models.BixbyError) => {
  return {
    type: SAVE_CLIENT_EBITDA_FAILURE,
    payload,
  };
};

export const loadCreateDealNoteAction = () => {
  return {
    type: CREATE_DEAL_NOTE_LOADING,
  };
};

export const setCreateDealNoteSuccessAction = (payload?: Bixby.Models.DealNote[]) => {
  return {
    type: CREATE_DEAL_NOTE_SUCCESS,
    payload,
  };
};

export const setCreateDealNoteFailureAction = (payload: Bixby.Models.BixbyError) => {
  return {
    type: CREATE_DEAL_NOTE_FAILURE,
    payload,
  };
};

export const loadDealNotesAction = () => {
  return {
    type: DEAL_NOTES_LOADING,
  };
};

export const setDealNotesSuccessAction = (payload?: Bixby.Models.DealNote[]) => {
  return {
    type: DEAL_NOTES_SUCCESS,
    payload,
  };
};

export const setDealNotesFailureAction = (payload: Bixby.Models.BixbyError) => {
  return {
    type: DEAL_NOTES_FAILURE,
    payload,
  };
};

// thunk actions

export const getPortfolio =
  (myPortfolioEnabled: boolean): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(loadPortfolioAction());
    try {
      const portfolio = await CompanyService.getPortfolio(myPortfolioEnabled);
      dispatch(portfolioSuccessAction(portfolio));
    } catch (error: any) {
      dispatch(portfolioFailureAction(error));
    }
  };

export const getCompany =
  (
    portfolioCompany: Bixby.Models.CompanyPortfolio,
    draft: boolean
  ): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(loadCompanyAction());
    try {
      const company = await CompanyService.getCompanyById(portfolioCompany.companyId, draft);
      dispatch(companySuccessAction(company));
    } catch (error: any) {
      dispatch(companyFailureAction(error));
    }
  };

export const getCompanyFinancials =
  (
    portfolioCompany: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ): ThunkAction<Promise<string[]>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(
      loadCompanyFinancialAction({
        isDraft: false,
        financialStatementType: statementType,
      })
    );
    try {
      const financials = await CompanyService.getCompanyFinancialStatement(portfolioCompany.id, statementType);
      dispatch(
        companyFinancialSuccessAction({
          isDraft: false,
          financialStatementType: statementType,
          isLoading: false,
          data: financials,
        })
      );
      return financials;
    } catch (error: any) {
      dispatch(
        companyFinancialFailureAction({
          isDraft: false,
          financialStatementType: statementType,
          error: error,
        })
      );
      return [];
    }
  };

export const getDraftCompanyFinancials =
  (
    portfolioCompany: Bixby.Models.Company,
    statementType: Bixby.Enums.FinancialStatementType
  ): ThunkAction<Promise<string[]>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(
      loadCompanyFinancialAction({
        isDraft: true,
        financialStatementType: statementType,
      })
    );
    try {
      const financials = await DraftCompanyService.getDraftFinancialStatement(portfolioCompany.id, statementType);
      dispatch(
        companyFinancialSuccessAction({
          isDraft: true,
          financialStatementType: statementType,
          isLoading: false,
          data: financials,
        })
      );
      return financials;
    } catch (error: any) {
      dispatch(
        companyFinancialFailureAction({
          isDraft: true,
          financialStatementType: statementType,
          error: error,
        })
      );
      return [];
    }
  };

export const setAlertsEnabled =
  (companyId: string, alertsEnabled: boolean): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    try {
      await AccountService.updateAccountUserCompanyAlertState(companyId, alertsEnabled);
      dispatch(setAlertsEnabledSuccessAction(companyId));
    } catch (error: any) {
      dispatch(setAlertsEnabledFailureAction(error));
    }
  };

export const saveClientEbitda =
  (
    portfolioCompany: Bixby.Models.Company,
    model: Bixby.Models.CompanyClientEbitda
  ): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(loadSaveClientEbitdaAction());
    try {
      const company = await CompanyService.saveClientEbitda(portfolioCompany.id, model);
      dispatch(setSaveClientEbitdaSuccessAction(company));
      dispatch(toastSuccess(`EBITDA saved`));
    } catch (error: any) {
      dispatch(setSaveClientEbitdaFailureAction(error));
      dispatch(toastError(`EBITDA failed to save`));
    }
  };

export const createDealNote =
  (clientCompanyId: string, note: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(loadCreateDealNoteAction());
    try {
      const dealNotes = await CompanyService.createDealNote(clientCompanyId, {
        note: note,
      } as Bixby.Models.CreateNote);
      dispatch(setCreateDealNoteSuccessAction(dealNotes));
      dispatch(toastSuccess(`Deal Note saved`));
    } catch (error: any) {
      dispatch(setCreateDealNoteFailureAction(error));
      dispatch(toastError(`Deal Note failed to save`));
    }
  };

export const getDealNotes =
  (clientCompanyId: string): ThunkAction<Promise<void>, AppState, null, Bixby.Models.BixbyAction> =>
  async dispatch => {
    dispatch(loadDealNotesAction());
    try {
      const dealNotes = await CompanyService.getDealNotes(clientCompanyId);
      dispatch(setDealNotesSuccessAction(dealNotes));
    } catch (error: any) {
      dispatch(setDealNotesFailureAction(error));
    }
  };
